<script setup lang="ts">
import { TintedIcon } from "@innoclouds/ui";
import { useElementSize, useMediaQuery } from "@vueuse/core";
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { SIZE_ICON_INACTIVE_COLOR, SIZE_ICON_SIZE } from "#/constants/sizeIcons";
import { useCardStore } from "#/store/card";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import { isVectorIcon } from "#/utils/badges";
import { formatPrice, getLocaleAltName } from "#/utils/i18next";

const cardStore = useCardStore();
const themeStore = useThemeStore();
const mainStore = useMainStore();
const { stateCard } = storeToRefs(cardStore);

const { i18next } = useTranslation();

const activeItemPosition = ref(50);
const sizeActive = ref<any>();
const currentIndex = ref(0);

const maxVisibleItems = 3;
const sliderWrapper = ref<HTMLElement | null>(null);
const { width: sliderWidth } = useElementSize(sliderWrapper);

const ICON_SIZE = SIZE_ICON_SIZE - 10;
const LARGE_SCREEN_ICON_SCALE = 1.5;

const isLargeScreen = useMediaQuery("(min-width: 768px)");
const iconSize = computed(() => Math.round(isLargeScreen.value ? ICON_SIZE * LARGE_SCREEN_ICON_SCALE : ICON_SIZE));

const showPrices = computed(() => themeStore.data?.show_size_picker_prices);
const hasIcons = computed(() => stateCard.value.productSizes.some((size: any) => !!size.size_image));
const visibleSizesCount = computed(() => Math.min(stateCard.value.productSizes.length, maxVisibleItems));
const iconsExtraWidth = computed(() => (hasIcons.value ? visibleSizesCount.value * iconSize.value : 0));
const itemWidth = computed(() => sliderWidth.value / (visibleSizesCount.value || 1));
const activeItemWidth = computed(() => Math.max(itemWidth.value - 8, 0));
const activeItemStyle = computed(() => {
	if (stateCard.value.productSizes.length < 4) {
		return { left: `${activeItemPosition.value}%`, width: `${activeItemWidth.value}px` };
	}
	return { left: `50%`, transform: `translate(-50%, -50%)`, width: `${activeItemWidth.value}px` };
});

const centerItem = computed(() => {
	if (stateCard.value.productSizes.length < 4) {
		return stateCard.value.productSizes[sizeActive.value.index] || stateCard.value.productSizes[0];
	}
	const centerIndex = currentIndex.value + 1;
	const originalIndex = extendedSizes.value[centerIndex]?.originalIndex ?? 1;

	return stateCard.value.productSizes[originalIndex];
});

const extendedSizes = computed(() => {
	if (stateCard.value.productSizes?.length < 4) return stateCard.value.productSizes;
	const sizes = stateCard.value.productSizes || [];
	return [
		...sizes.map((size: any, index: number) => ({ ...size, originalIndex: index })),
		...sizes.map((size: any, index: number) => ({ ...size, originalIndex: index })),
		...sizes.map((size: any, index: number) => ({ ...size, originalIndex: index })),
	];
});

const changeSize = (item: any, sliderIndex: number) => {
	sizeActive.value = {
		id: item.id,
		title: item.name,
		index: sliderIndex,
		is_default: item.is_default,
	};

	if (item.price !== null && stateCard.value.openCard) {
		stateCard.value.openCard = {
			...stateCard.value.openCard,
			price: item.price,
		};
	}
	stateCard.value.selectedSize = item;

	if (stateCard.value.productSizes.length >= 4) {
		const centerIndex = currentIndex.value + 1;
		if (sliderIndex < centerIndex) {
			slideLeft();
		} else if (sliderIndex > centerIndex) {
			slideRight();
		}
	} else {
		const itemCount = stateCard.value.productSizes.length;
		const positionPercentage = sliderIndex * (100 / itemCount);
		activeItemPosition.value = positionPercentage;
	}
};

const slideLeft = () => {
	if (stateCard.value.productSizes.length < 4) return;
	currentIndex.value--;
	if (currentIndex.value < stateCard.value.productSizes.length - maxVisibleItems + 1) {
		currentIndex.value += stateCard.value.productSizes.length;
		const ul = sliderWrapper.value?.querySelector(".card_size") as HTMLElement;
		if (ul) {
			ul.style.transition = "none";
			ul.style.transform = `translateX(-${currentIndex.value * itemWidth.value}px)`;
			ul.offsetHeight;
			ul.style.transition = "transform 0.2s ease-in-out";
		}
	}
	updateActiveItem();
};

const slideRight = () => {
	if (stateCard.value.productSizes.length < 4) return;
	currentIndex.value++;
	if (currentIndex.value >= stateCard.value.productSizes.length * 2) {
		currentIndex.value -= stateCard.value.productSizes.length;
		const ul = sliderWrapper.value?.querySelector(".card_size") as HTMLElement;
		if (ul) {
			ul.style.transition = "none";
			ul.style.transform = `translateX(-${currentIndex.value * itemWidth.value}px)`;
			ul.offsetHeight;
			ul.style.transition = "transform 0.2s ease-in-out";
		}
	}
	updateActiveItem();
};

const updateActiveItem = () => {
	if (stateCard.value.productSizes.length < 4) return;
	const centerIndex = currentIndex.value + 1;
	const originalIndex = extendedSizes.value[centerIndex]?.originalIndex ?? 0;
	const size = stateCard.value.productSizes[originalIndex];
	sizeActive.value = {
		id: size.id,
		title: size.name,
		index: originalIndex,
		is_default: size.is_default,
	};
};

watch(
	[() => stateCard.value.productSizes, sliderWrapper],
	([sizes = []]) => {
		if (!Array.isArray(sizes) || sizes.length === 0) return;

		const selectedSizeId = stateCard.value.selectedSize?.id;
		const preservedIndex = selectedSizeId ? sizes.findIndex((item: any) => item.id === selectedSizeId) : -1;

		if (preservedIndex > -1) {
			currentIndex.value = preservedIndex;
			changeSize(sizes[preservedIndex], preservedIndex);
			return;
		}

		let defaultIndex = sizes.findIndex((item: any) => item.is_default === true);
		if (defaultIndex > -1) {
			currentIndex.value = defaultIndex;
			changeSize(sizes[defaultIndex], defaultIndex);
		} else if (sizes.length > maxVisibleItems) {
			changeSize(sizes[1], 1);
		} else {
			changeSize(sizes[0], 0);
		}
	},
	{ deep: true, immediate: true },
);

watch(
	[() => stateCard.value.selectedSize?.id, () => stateCard.value.groupModifiersSelected],
	([selectedSizeId, groupModifiersSelected]) => {
		if (!selectedSizeId || !Array.isArray(groupModifiersSelected)) return;
		const updatedGroups = groupModifiersSelected.map((group) => ({
			...group,
			modifiers: group.modifiers.map((mod) => {
				if (!mod.sizes || mod.sizes.length === 0) return mod;

				const selectSize = mod.sizes.find((s) => s.id === selectedSizeId);
				if (!selectSize) return mod;

				return { ...mod, size: selectSize, price: selectSize.price ?? mod.price };
			}),
		}));
		if (JSON.stringify(updatedGroups) !== JSON.stringify(stateCard.value.groupModifiersSelected)) {
			stateCard.value.groupModifiersSelected = updatedGroups;
		}
	},
	{ deep: true, immediate: true },
);
</script>

<template>
	<div
		class="slider_container"
		v-if="stateCard.productSizes?.length > 1 && sizeActive"
		:style="{ '--size_icons_extra': `${iconsExtraWidth}px`, '--size_icon': `${iconSize}px` }"
	>
		<div class="card_size_place_wrapper" ref="sliderWrapper">
			<div class="card_size_place" v-if="stateCard.productSizes.length > 1 && sizeActive">
				<ul v-if="stateCard.productSizes.length > 3" class="card_size" :style="{ transform: `translateX(-${currentIndex * itemWidth}px)` }">
					<li
						v-for="(size, index) in extendedSizes"
						:key="`${size.id}-${index}`"
						@click="changeSize(size, index)"
						:class="{ active: sizeActive.id === size.id && sizeActive.index === size.originalIndex, card_size_item_with_icon: hasIcons }"
						tabindex="0"
						role="button"
					>
						<TintedIcon
							v-if="hasIcons && size.size_image"
							:src="size.size_image"
							:vector="isVectorIcon(size.size_image)"
							:color="SIZE_ICON_INACTIVE_COLOR"
							:size="iconSize"
						/>
						<p>{{ getLocaleAltName(size, i18next.language) }}</p>
						<span v-if="showPrices" class="card_size_price">{{ formatPrice(size.price ?? 0, mainStore.config_lib?.currency_symbol) }}</span>
					</li>
				</ul>
				<ul v-else class="card_size static">
					<li
						v-for="(size, index) in stateCard.productSizes || []"
						:key="`${size.id}-${index}`"
						@click="changeSize(size, index)"
						:class="{ active: sizeActive.id === size.id && sizeActive.index === index, card_size_item_with_icon: hasIcons }"
						tabindex="0"
						role="button"
					>
						<TintedIcon
							v-if="hasIcons && size.size_image"
							:src="size.size_image"
							:vector="isVectorIcon(size.size_image)"
							:color="SIZE_ICON_INACTIVE_COLOR"
							:size="iconSize"
						/>
						<p>{{ getLocaleAltName(size, i18next.language) }}</p>
						<span v-if="showPrices" class="card_size_price">{{ formatPrice(size.price ?? 0, mainStore.config_lib?.currency_symbol) }}</span>
					</li>
				</ul>
				<div class="size_active" :class="{ card_size_item_with_icon: hasIcons }" :style="activeItemStyle">
					<TintedIcon
						v-if="hasIcons && centerItem?.size_image"
						:key="centerItem?.id"
						:src="centerItem?.size_image"
						:vector="isVectorIcon(centerItem?.size_image)"
						:color="centerItem?.icon_color || null"
						:size="iconSize"
					/>
					<p>{{ getLocaleAltName(centerItem, i18next.language) }}</p>
					<span v-if="showPrices" class="card_size_price">{{ formatPrice(centerItem.price ?? 0, mainStore.config_lib?.currency_symbol) }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
  @use './sizeModifier.scss';
</style>
